import type { TFunction } from "i18next";
import type { ManualBody } from "@arc/contracts";

/**
 * "Check Manual" — the legacy's pre-publish validation (ADR-018 addendum §2/§4).
 *
 * A PURE function over the body the editor already holds: no React, no fetch, no server round-trip. It is **advisory**,
 * exactly as the legacy is — the dialog offers "Continue anyway" and the author decides. The server keeps its own authz
 * and state CAS regardless; if a HARD rule is ever wanted it has to become a server invariant, because a client check
 * can always be bypassed.
 *
 * THREE checks, not the legacy's five:
 * - **Not-flagged is gone for good.** The legacy tests `isMarked`, a boolean orthogonal to publish state. ADR-018 made
 *   `marked` a mutually-exclusive STATE reachable only as `confirmed → marked`, and the gate runs on the
 *   publish-direction transitions — so the check is structurally always true. A row that cannot fail is worse than no
 *   row: a checklist is trusted precisely because its rows can.
 * - **Unused-files is v2**, gated on the per-manual project folder (§5). Deliberately not shown at all — neither faked
 *   as a pass nor greyed out, because an author should not see a check they cannot act on.
 */
export type ManualCheckKey = "description" | "sections-have-steps" | "steps-have-text";

export interface ManualCheckResult {
  key: ManualCheckKey;
  ok: boolean;
  /** The row's sentence, phrased for the state it is in (the legacy has a distinct string for pass and fail). */
  label: string;
  /** What the check is for — the legacy puts this behind a "?" button. */
  detail: string;
  /** What failed, named. The legacy computes these and shows them in its "?" pop-up as `Section / Step`. */
  offenders: string[];
}

/** Cap the named offenders — a manual with 50 empty steps should not produce a 50-line dialog row. */
const MAX_OFFENDERS = 10;

/**
 * Visible text, not markup.
 *
 * A step's text is sanitized rich text. `<p></p>` is already collapsed to `null` upstream, but a lone `<hr>` is
 * deliberately PRESERVED by the sanitizer — so a naive `Boolean(text?.trim())` would count a step containing only a
 * horizontal rule as "has text". Strip the tags and look for an actual character. `&nbsp;` counts as whitespace,
 * because that is what it looks like to the reader.
 */
function hasVisibleText(html: string | null | undefined): boolean {
  if (!html) return false;
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim().length > 0;
}

export function checkManual(body: ManualBody, t: TFunction): ManualCheckResult[] {
  const sections = body.sections;

  const emptySections = sections.filter((s) => s.steps.length === 0).map((s) => s.name);

  // `Section / Step`, the legacy's own format for these.
  const emptySteps = sections.flatMap((s) => s.steps.filter((st) => !hasVisibleText(st.text)).map((st) => `${s.name} / ${st.name}`));

  return [
    {
      key: "description",
      ok: hasVisibleText(body.manual.description),
      label: hasVisibleText(body.manual.description) ? t("manualEditor.check.hasDescription") : t("manualEditor.check.noDescription"),
      detail: "A description is what a reader sees before they start, and what search matches on.",
      offenders: [],
    },
    {
      key: "sections-have-steps",
      ok: emptySections.length === 0,
      label: emptySections.length === 0 ? t("manualEditor.check.sectionsHaveSteps") : t("manualEditor.check.sectionsEmpty"),
      detail: "A section with no steps is skipped entirely in the reader — it reads as a gap.",
      offenders: emptySections.slice(0, MAX_OFFENDERS),
    },
    {
      key: "steps-have-text",
      ok: emptySteps.length === 0,
      label: emptySteps.length === 0 ? t("manualEditor.check.stepsHaveText") : t("manualEditor.check.stepsEmpty"),
      // The legacy warns on ANY step with empty `text`, including typed ones (question / checklist / score) whose
      // content lives in the payload and may legitimately leave `text` blank. v1 mirrors that rather than inventing a
      // rule; treating "has a payload" as satisfying it is a later refinement (addendum §2 note).
      detail: "A step with no text gives the reader nothing to act on. Question and checklist steps may carry their content in the answers instead.",
      offenders: emptySteps.slice(0, MAX_OFFENDERS),
    },
  ];
}

/** True when nothing warns — drives "Continue" vs "Continue anyway". */
export const manualCheckPassed = (results: ManualCheckResult[]): boolean => results.every((r) => r.ok);
